import React, { useEffect, useState } from 'react';
import axios from 'axios';
import WelcomeCard from '../../components/common/WelcomeCard';
import { LiaImagesSolid } from "react-icons/lia";
import { FaEdit, FaTrash } from 'react-icons/fa';
import Loader from '../../components/common/Loader';
import toast from 'react-hot-toast';

// Add Banner Modal Component
const AddBannerModal = ({ open, handleClose, allBanners }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);

  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", process.env.REACT_APP_UPLOAD_PRESET);

    const res = await axios.post(
      `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUD_NAME}/image/upload`,
      formData
    );
    return res.data.secure_url;
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;
    setLoading(true);
    try {
      const uploadPromises = Array.from(files).map((file) =>
        uploadToCloudinary(file)
      );
      const uploadedUrls = await Promise.all(uploadPromises);
      setImages([...images, ...uploadedUrls]);
      toast.success("Images uploaded successfully");
    } catch (err) {
      toast.error("Image upload failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (images.length === 0) {
      toast.error("Please upload at least one image");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/banner/add-banner`, {
        images
      });
      toast.success("Banner added successfully");
      allBanners();
      handleClose();
    } catch (error) {
      console.error("Error adding banner:", error);
      toast.error("Failed to add banner");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold mb-4">Add New Banner</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Image Upload */}
          <div>
            <label className="block mb-2 font-medium">
              Banner Images * (1200×600 size)
            </label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              className="mb-2"
            />
            <div className="flex flex-wrap gap-2">
              {images.map((img, i) => (
                <div key={i} className="relative">
                  <img
                    src={img}
                    alt="banner"
                    className="w-20 h-20 object-cover rounded border"
                  />
                  <button
                    type="button"
                    onClick={() => setImages(images.filter((_, index) => index !== i))}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4 pt-4 border-t">
            <button
              type="button"
              onClick={handleClose}
              className="bg-gray-300 hover:bg-gray-400 px-6 py-2 rounded"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded disabled:opacity-50"
            >
              {loading ? <Loader /> : "Add Banner"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Banner Modal Component
const EditBannerModal = ({ open, banner, handleClose, allBanners }) => {
  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (banner) {
      setExistingImages(banner.images || []);
      setNewImages([]);
    }
  }, [banner]);

  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", process.env.REACT_APP_UPLOAD_PRESET);

    const res = await axios.post(
      `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUD_NAME}/image/upload`,
      formData
    );
    return res.data.secure_url;
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;
    setLoading(true);
    try {
      const uploadPromises = Array.from(files).map((file) =>
        uploadToCloudinary(file)
      );
      const uploadedUrls = await Promise.all(uploadPromises);
      setNewImages([...newImages, ...uploadedUrls]);
      toast.success("Images uploaded successfully");
    } catch (err) {
      toast.error("Image upload failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const finalImages = [...existingImages, ...newImages];

    if (finalImages.length === 0) {
      toast.error("Please add at least one image");
      return;
    }

    setLoading(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/banner/update-banner/${banner._id}`,
        { images: finalImages }
      );
      toast.success("Banner updated successfully");
      allBanners();
      handleClose();
    } catch (error) {
      console.error("Error updating banner:", error);
      toast.error("Failed to update banner");
    } finally {
      setLoading(false);
    }
  };

  if (!open || !banner) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold mb-4">Edit Banner</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Existing Images */}
          <div>
            <label className="block mb-2 font-medium">Existing Images* (1200×600 size)</label>
            <div className="flex flex-wrap gap-2 mb-4">
              {existingImages.map((img, i) => (
                <div key={i} className="relative">
                  <img
                    src={img}
                    alt="banner"
                    className="w-20 h-20 object-cover rounded border"
                  />
                  <button
                    type="button"
                    onClick={() => setExistingImages(existingImages.filter((_, index) => index !== i))}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* New Images */}
          <div>
            <label className="block mb-2 font-medium">Add New Images</label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              className="mb-2"
            />
            <div className="flex flex-wrap gap-2">
              {newImages.map((img, i) => (
                <div key={i} className="relative">
                  <img
                    src={img}
                    alt="banner"
                    className="w-20 h-20 object-cover rounded border"
                  />
                  <button
                    type="button"
                    onClick={() => setNewImages(newImages.filter((_, index) => index !== i))}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4 pt-4 border-t">
            <button
              type="button"
              onClick={handleClose}
              className="bg-gray-300 hover:bg-gray-400 px-6 py-2 rounded"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded disabled:opacity-50"
            >
              {loading ? <Loader /> : "Update Banner"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Main Banner Component
const Banner = () => {
  const [bannerList, setBannerList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);

  useEffect(() => {
    allBanners();
  }, []);

  const allBanners = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/banner/get-all-banner`);
      setBannerList(response.data);
    } catch (error) {
      console.error("Error fetching banners:", error);
      toast.error("Failed to load banners");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddModal = () => setOpenAddModal(true);
  const handleCloseAddModal = () => setOpenAddModal(false);

  const handleOpenEditModal = (banner) => {
    setSelectedBanner(banner);
    setOpenEditModal(true);
  };
  const handleCloseEditModal = () => {
    setSelectedBanner(null);
    setOpenEditModal(false);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this banner?')) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL}/banner/delete-banner/${id}`);
        toast.success("Banner deleted successfully");
        allBanners();
      } catch (error) {
        console.error("Error deleting banner:", error);
        toast.error("Failed to delete banner");
      }
    }
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

  return (
    <>
      <AddBannerModal
        open={openAddModal}
        handleClose={handleCloseAddModal}
        allBanners={allBanners}
      />

      {selectedBanner && (
        <EditBannerModal
          open={openEditModal}
          banner={selectedBanner}
          handleClose={handleCloseEditModal}
          allBanners={allBanners}
        />
      )}

      <div className='p-4'>
        <WelcomeCard />

        <div className='flex justify-between items-center mb-4'>
          <h1 className="text-xl font-semibold text-[#000000] tracking-wider">Banners List</h1>

          <button
            onClick={handleOpenAddModal}
            className="flex justify-center items-center bg-gradient-to-r from-[#FF3131] to-[#ff9301] text-white font-semibold tracking-wider py-2 px-4 rounded-md hover:bg-red-600 transition duration-300 ease-in-out"
          >
            <LiaImagesSolid size={24} className='mr-2' /> Add New Banner
          </button>
        </div>

        {loading ? (
          <div className='rounded-md flex tracking-wider font-semibold justify-center items-center border border-slate-400 w-full p-4'>
            <Loader /> <span className='ml-2'>Loading Banners...</span>
          </div>
        ) : bannerList.length === 0 ? (
          <div className='rounded-md flex tracking-wider font-semibold justify-center items-center border border-slate-400 w-full p-4'>
            No banners found.
          </div>
        ) : (
          <div className="container mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {bannerList.map(banner => (
                <div key={banner._id} className="bg-white shadow-md rounded-lg overflow-hidden transition transform hover:scale-105 relative">
                  {/* Display first image as thumbnail */}
                  {banner.images && banner.images.length > 0 && (
                    <img
                      src={banner.images[0]}
                      alt="Banner"
                      className="w-full h-48 object-cover object-center"
                    />
                  )}

                  <div className="p-4">
                    <p className="text-gray-600 text-sm mb-2">
                      Created: {formatDate(banner.createdAt)}
                    </p>
                    <p className="text-gray-600 text-sm mb-4">
                      Images: {banner.images?.length || 0}
                    </p>

                    <div className="flex justify-end space-x-4">
                      <button
                        onClick={() => handleOpenEditModal(banner)}
                        className="text-blue-500 hover:text-blue-700"
                      >
                        <FaEdit size={20} />
                      </button>

                      <button
                        onClick={() => handleDelete(banner._id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <FaTrash size={20} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Banner;